import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import CustomModal from "../Common/CustomModal";
import { colors, fonts } from "../../theme/theme";

const fieldStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    fontFamily: fonts.gotham,
    fontSize: "14px",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.secondary,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.secondary,
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: fonts.gotham,
    fontSize: "14px",
    "&.Mui-focused": {
      color: colors.secondary,
    },
  },
};

const ComposeMessageModal = ({
  open,
  handleClose,
  contacts = [],
  preSelectedContact = null,
  onSend,
  sending = false,
}) => {
  const [selectedContact, setSelectedContact] = useState(
    preSelectedContact || null,
  );
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [error, setError] = useState("");

  // Sync internal state when preSelectedContact prop changes (e.g. practitioner
  // clicks "Message Client" on a different patient while modal stays mounted)
  useEffect(() => {
    setSelectedContact(preSelectedContact || null);
  }, [preSelectedContact]);

  const handleSend = async () => {
    setError("");

    if (!selectedContact) {
      setError("Please select a recipient.");
      return;
    }
    if (!subject.trim()) {
      setError("Please enter a subject.");
      return;
    }
    if (!body.trim()) {
      setError("Please enter a message.");
      return;
    }

    try {
      await onSend({
        toUserId: selectedContact.id,
        subject: subject.trim(),
        body: body.trim(),
      });

      // Reset form on success
      setSubject("");
      setBody("");
      if (!preSelectedContact) {
        setSelectedContact(null);
      }
    } catch (err) {
      setError(err.message || "Failed to send message. Please try again.");
    }
  };

  const handleCancel = () => {
    setSubject("");
    setBody("");
    setError("");
    if (!preSelectedContact) {
      setSelectedContact(null);
    }
    handleClose();
  };

  return (
    <CustomModal
      open={open}
      handleClose={handleCancel}
      title="Compose Message"
      saveBtnText={sending ? "Sending..." : "Send"}
      cancelBtnText="Cancel"
      onSave={handleSend}
      onCancel={handleCancel}
      height="auto"
      width="600px"
      disableBtn={sending}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          p: 3,
        }}
      >
        {error && (
          <Typography
            sx={{
              color: "#d32f2f",
              fontFamily: fonts.gotham,
              fontSize: "13px",
              p: 1.5,
              borderRadius: "8px",
              backgroundColor: "rgba(211, 47, 47, 0.06)",
              border: "1px solid rgba(211, 47, 47, 0.2)",
            }}
          >
            {error}
          </Typography>
        )}

        <Autocomplete
          value={selectedContact}
          onChange={(_, newValue) => setSelectedContact(newValue)}
          options={contacts}
          getOptionLabel={(option) =>
            `${option.firstName || ""} ${option.lastName || ""}`.trim() ||
            option.email
          }
          isOptionEqualToValue={(option, value) => option.id === value.id}
          disabled={!!preSelectedContact || sending}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              key={option.id}
              sx={{ fontFamily: fonts.gotham, fontSize: "14px" }}
            >
              <Box>
                <Typography
                  sx={{
                    fontFamily: fonts.gotham,
                    fontWeight: 600,
                    fontSize: "14px",
                  }}
                >
                  {`${option.firstName || ""} ${option.lastName || ""}`.trim() ||
                    "N/A"}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: fonts.gotham,
                    fontSize: "12px",
                    color: colors.textLight,
                  }}
                >
                  {option.email}
                </Typography>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="To"
              placeholder="Select a recipient"
              sx={fieldStyle}
            />
          )}
        />

        <TextField
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          fullWidth
          disabled={sending}
          sx={fieldStyle}
        />

        <TextField
          label="Message"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          fullWidth
          multiline
          rows={8}
          disabled={sending}
          placeholder="Type your message here..."
          sx={fieldStyle}
        />

        {sending && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={16} sx={{ color: colors.secondary }} />
            <Typography
              sx={{
                fontFamily: fonts.gotham,
                fontSize: "13px",
                color: colors.textLight,
              }}
            >
              Sending your message...
            </Typography>
          </Box>
        )}
      </Box>
    </CustomModal>
  );
};

export default ComposeMessageModal;
